/**
 * Over-the-air (OTA) live-update control plane, stored in the single Firestore
 * doc `config/ota`. World-readable so the native app can read the release
 * manifest before sign-in; written ONLY by the Admin SDK (the password-gated
 * `setOtaConfig` callable and the `release-ota` ops script), never by clients.
 *
 * This is the football-engine sibling of `appConfig.ts`'s force-update gate: a
 * tiny, fully PURE decision (`selectOtaUpdate`) shared by the client updater so
 * the same rule decides "should this install pull a new web bundle?" everywhere.
 *
 * It governs ONLY the web bundle (HTML/CSS/JS) the Capacitor WebView loads. It
 * can never change native code — that always needs an App Store submission.
 */
import { compareVersions } from './appConfig.js';
/**
 * The single pure rule the client updater follows: given the live manifest and
 * this install's state, should it download + apply a new web bundle, and which?
 *
 * Fail-CLOSED by design — it returns `apply: false` unless EVERY guard passes:
 * the platform is native, the gate is explicitly enabled, the manifest is
 * complete (version + url), the binary is new enough for the bundle
 * (`minNativeBuild`), and the bundle is STRICTLY newer than what's running. So a
 * config slip, a stale pointer, or a half-written manifest can never downgrade an
 * install or push an incompatible bundle.
 */
export function selectOtaUpdate(cfg, installed) {
    if (!installed.native)
        return { apply: false, reason: 'not-native' };
    if (!cfg || cfg.enabled !== true)
        return { apply: false, reason: 'disabled' };
    const version = typeof cfg.version === 'string' ? cfg.version.trim() : '';
    const url = typeof cfg.url === 'string' ? cfg.url.trim() : '';
    if (!version || !url)
        return { apply: false, reason: 'incomplete' };
    const hasMinNative = typeof cfg.minNativeBuild === 'number' && Number.isFinite(cfg.minNativeBuild);
    if (hasMinNative &&
        Number.isFinite(installed.build) &&
        installed.build < cfg.minNativeBuild) {
        return { apply: false, reason: 'native-too-old' };
    }
    // Strictly newer only — equal or older never applies (no re-download, no downgrade).
    if (compareVersions(version, installed.currentVersion) <= 0) {
        return { apply: false, reason: 'not-newer' };
    }
    const checksum = typeof cfg.checksum === 'string' && cfg.checksum.trim() ? cfg.checksum.trim() : undefined;
    return { apply: true, version, url, checksum, mandatory: cfg.mandatory === true };
}
/**
 * Sanitise one raw {@link OtaBundleRef} (e.g. from an admin write or the release
 * script). Returns `null` for anything missing the two load-bearing fields
 * (version + url) so junk never enters the upload history.
 */
function normalizeBundleRef(input) {
    const raw = (input && typeof input === 'object' ? input : {});
    const version = typeof raw.version === 'string' ? raw.version.trim() : '';
    const url = typeof raw.url === 'string' ? raw.url.trim() : '';
    if (!version || !url)
        return null;
    const ref = { version, url, at: Number(raw.at) || Date.now() };
    const checksum = typeof raw.checksum === 'string' ? raw.checksum.trim() : '';
    if (checksum)
        ref.checksum = checksum.toLowerCase();
    const minNativeBuild = Number(raw.minNativeBuild);
    if (Number.isFinite(minNativeBuild) && minNativeBuild > 0) {
        ref.minNativeBuild = Math.floor(minNativeBuild);
    }
    return ref;
}
/** Sanitise the `available` upload history, dropping invalid entries. */
export function normalizeBundleList(input) {
    if (!Array.isArray(input))
        return [];
    return input
        .map(normalizeBundleRef)
        .filter((ref) => ref !== null);
}
/**
 * Sanitise raw admin/script input into a clean {@link OtaConfig} for storage:
 * coerce types, trim strings, drop blanks. Only keys carrying a real value
 * (besides `enabled`) are kept, so clearing a field actually removes it rather
 * than persisting an empty string. Mirrors `normalizeForceUpdateConfig`.
 */
export function normalizeOtaConfig(input) {
    const raw = (input && typeof input === 'object' ? input : {});
    const str = (v) => (typeof v === 'string' ? v.trim() : '');
    const out = { enabled: raw.enabled === true };
    const version = str(raw.version);
    if (version)
        out.version = version;
    const url = str(raw.url);
    if (url)
        out.url = url;
    const checksum = str(raw.checksum);
    if (checksum)
        out.checksum = checksum.toLowerCase();
    const minNativeBuild = Number(raw.minNativeBuild);
    if (Number.isFinite(minNativeBuild) && minNativeBuild > 0) {
        out.minNativeBuild = Math.floor(minNativeBuild);
    }
    if (raw.mandatory === true)
        out.mandatory = true;
    const notes = str(raw.notes);
    if (notes)
        out.notes = notes;
    const available = normalizeBundleList(raw.available);
    if (available.length)
        out.available = available;
    return out;
}
//# sourceMappingURL=ota.js.map